import { Button, Flex } from "@mantine/core";
import { useState, useCallback } from "react";
import type { InputOption } from "@/types";
import {
  translateIdentity,
  type StaticTextTranslator,
} from "@/utils/staticTextTranslator";
import styles from "./MultipleChoice.module.css";

interface MultipleChoiceProps {
  /** Whether to show the "Done selecting" button (default: true) */
  confirmSelection?: boolean;
  /** Whether this is a multi-select (checkboxes) or single-select (radio) */
  multiSelect?: boolean;
  /** Callback when selection is submitted */
  onSubmit: (selected: InputOption | InputOption[]) => void;
  /** Callback when the active selection changes before submit */
  onSelectionChange?: (selected: InputOption[]) => void;
  /** Available options */
  options: InputOption[];
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Whether to use the compact local benchmark appearance */
  isBenchmark?: boolean;
  /** Pre-selection for edit-mode prefill; read once at mount (parent
   * key-remounts on each edit entry). */
  initialSelectedOptionIds?: string[];
  translate?: StaticTextTranslator;
}

/**
 * Multiple choice / single choice selection component
 * Uses pill-style buttons matching the original screener design
 */
export const MultipleChoice = ({
  confirmSelection = true,
  multiSelect = false,
  onSubmit,
  onSelectionChange,
  options,
  disabled = false,
  isBenchmark = false,
  initialSelectedOptionIds,
  translate = translateIdentity,
}: MultipleChoiceProps) => {
  const [selectedOptions, setSelectedOptions] = useState<InputOption[]>(() =>
    initialSelectedOptionIds?.length
      ? options.filter((option) => initialSelectedOptionIds.includes(option.id))
      : [],
  );

  const handleOptionClick = useCallback(
    (option: InputOption) => {
      if (disabled) return;

      const isCurrentlySelected = selectedOptions.some(
        (o) => o.id === option.id,
      );

      if (!multiSelect) {
        // Single select - replace selection
        const nextSelectedOptions = [option];
        setSelectedOptions(nextSelectedOptions);
        onSelectionChange?.(nextSelectedOptions);
        return;
      }

      // Multi-select - toggle
      let nextSelectedOptions: InputOption[];
      if (isCurrentlySelected) {
        nextSelectedOptions = selectedOptions.filter((o) => o.id !== option.id);
      } else {
        nextSelectedOptions = [...selectedOptions, option];
      }
      setSelectedOptions(nextSelectedOptions);
      onSelectionChange?.(nextSelectedOptions);
    },
    [disabled, multiSelect, onSelectionChange, selectedOptions],
  );

  const handleSubmit = useCallback(() => {
    if (selectedOptions.length === 0) return;

    const firstOption = selectedOptions[0];
    if (multiSelect) {
      onSubmit(selectedOptions);
    } else if (firstOption) {
      onSubmit(firstOption);
    }
  }, [selectedOptions, multiSelect, onSubmit]);

  const isSelected = (optionId: string) =>
    selectedOptions.some((o) => o.id === optionId);

  return (
    <Flex
      align="flex-end"
      className={isBenchmark ? styles.benchmarkOptions : undefined}
      data-variant={isBenchmark ? "benchmark" : undefined}
      direction={isBenchmark ? "row" : "column"}
      style={
        isBenchmark ? { flexDirection: "row", flexWrap: "wrap" } : undefined
      }
      wrap={isBenchmark ? "wrap" : "nowrap"}
    >
      <p className={styles.explanatoryText}>
        {multiSelect
          ? translate("Select as many as you want")
          : translate("Select one")}
      </p>

      {options.map((option) => (
        <div key={option.id} className={styles.optionContainer}>
          <label
            className={`${styles.optionLabel} ${isSelected(option.id) ? styles.optionSelected : ""}`}
            data-selection-type={multiSelect ? "multiple" : "single"}
            htmlFor={option.id}
          >
            <input
              className={styles.optionInput}
              type={multiSelect ? "checkbox" : "radio"}
              id={option.id}
              name={multiSelect ? "multiple-choice" : "single-select"}
              checked={isSelected(option.id)}
              onChange={() => handleOptionClick(option)}
              disabled={disabled}
            />
            {translate(option.optionText)}
          </label>
        </div>
      ))}

      {confirmSelection && (
        <Button
          className={styles.doneButton}
          data-testid="multiple-choice-done"
          disabled={selectedOptions.length < 1 || disabled}
          onClick={handleSubmit}
          unstyled
        >
          ✓ {translate("Done selecting")}
        </Button>
      )}
    </Flex>
  );
};
